import { VoyageRoutePointFeature, VoyageRouteLineFeature } from '../models/voyage-routing-model';
import { VoyagePlan } from '../models/vessel-planning-model';
import { knotsToMps, mpsToKnots } from '../utils/helpers';
import { ImportResult } from './types/converters.types';

export enum RTZVersion {
    RTZ_1_0 = "1.0",
    RTZ_1_2 = "1.2"
};

export class RTZConverter {
    static check(str: string) {
        try {
            const domParser = new DOMParser();
            var doc = domParser.parseFromString(str, "text/xml");

            const root = doc.firstElementChild;

            if (root && root.nodeName === 'route' &&
                (root.getAttribute('xmlns') === 'http://www.cirm.org/RTZ/1/0' ||
                 root.getAttribute('xmlns') === 'http://www.cirm.org/RTZ/1/2')) {
                return doc;
            }
        } catch(e) {
            // don't care
        }
    }

    static import(str: string): ImportResult|undefined {
        try {
            const route_geometry: Array<VoyageRoutePointFeature|VoyageRouteLineFeature> = [];

            const doc = RTZConverter.check(str);
            if (!doc) return;

            // Get defaultWaypoint for default values
            const dwp = RTZConverter._extractWP(doc.querySelector('defaultWaypoint'));

            const wps = Array.from(doc.querySelectorAll('waypoint'));

            // Loop through and extract waypoints and legs
            // Note: RTZ has leg information on the *end* waypoint (ie. the opposite of expected for our format)
            for (let i = 0; i < wps.length; i++)
            {
                const waypointEl = wps[i];
                const nextWPEl = i < wps.length - 1 ? wps[i+1] : null;

                const coords: [number, number] = this._getPosition(waypointEl);

                let nextWP: {[index:string]: string}|null = null;
                if (nextWPEl)
                {
                    nextWP = {
                        leg_geometryType: 'Loxodrome',  // Default to RL, per RTZ spec.
                        ...dwp,
                        ...RTZConverter._extractWP(nextWPEl)
                    };
                }

                const name = waypointEl.getAttribute('name') || '';

                const wPoint: VoyageRoutePointFeature = {
                    type: 'Feature',
                    geometry: {
                        coordinates: coords,
                        type: 'Point'
                    },
                    properties: {
                        type: 'Hard',
                        name
                    }
                };

                if (nextWP)
                {
                    if (nextWP["leg_speedMin"] && !isNaN(Number(nextWP["leg_speedMin"])))
                    {
                        wPoint.properties.speed_minimum = knotsToMps(Number(nextWP["leg_speedMin"]));
                    }
    
                    if (nextWP["leg_speedMax"] && !isNaN(Number(nextWP["leg_speedMax"])))
                    {
                        wPoint.properties.speed_maximum = knotsToMps(Number(nextWP["leg_speedMax"]));
                    }

                    // We only have one value for cross-track error, so only set it if port and starboard values are the same
                    if (nextWP["leg_starboardXTD"] && nextWP["leg_portsideXTD"] && Number(nextWP["leg_starboardXTD"]) === Number(nextWP["leg_portsideXTD"]))
                    {
                        wPoint.properties.max_x_track = `${Number(nextWP["leg_starboardXTD"])} NM`;
                    }
                }

                route_geometry.push(wPoint);

                if (nextWPEl && nextWP)
                {
                    route_geometry.push({
                            type: 'Feature',
                            geometry: {
                                coordinates: [coords, this._getPosition(nextWPEl)],
                                type: 'LineString'
                            },
                            properties: {
                                leg_type: nextWP['leg_geometryType'].toLowerCase() === 'orthodrome' ? 'GreatCircle' : 'Rhumbline'
                            }
                    });
                }
            };

            return {
                route: {route_geometry},
                extras: {
                    // Plan related data.

                }
            };
        } catch (error) {
            console.warn(error);
        }
    }

    static export(plan: VoyagePlan, filename: string, version: RTZVersion): string
    {
        // Bail out if no valid route is selected.
        if (plan.selectedRouteIndex < 0 || plan.selectedRouteIndex >= plan.routes.length)
        {
            throw new Error("No valid route selected in plan!");
        }
        
        // Get the selected route
        const route = plan.routes[plan.selectedRouteIndex];
        let xmlString;
        if (version == RTZVersion.RTZ_1_0)
        {
            xmlString = '<route xmlns="http://www.cirm.org/RTZ/1/0" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" version="1.0"/>';
        }
        else
        {
            xmlString = '<route xmlns="http://www.cirm.org/RTZ/1/2" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" version="1.2"/>';
        }

        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(xmlString, "text/xml"); //important to use "text/xml"
        const xmlRoute = xmlDoc.documentElement;
        
        const xmlRouteInfo = this._createXMLElement("routeInfo", xmlRoute);

        // For some ridiculous reason the routeName *has* to match the filename (no renaming files!)
        xmlRouteInfo.setAttribute("routeName", filename);
        const imo = (<any>window)["appenv"].vesselIMONumber;
        if (imo)
        {
            xmlRouteInfo.setAttribute("vesselIMO", imo);
        }

        const xmlWaypoints = this._createXMLElement("waypoints", xmlRoute);
        // Loop through and add waypoints
        let wpIdx = 1;
        for (let i = 0; i < route.route_geometry.length; i++)
        {
            if (route.route_geometry[i].geometry.type === "Point")
            {
                const point = <VoyageRoutePointFeature>route.route_geometry[i];
                const xmlWaypoint = this._createXMLElement("waypoint", xmlWaypoints);
                xmlWaypoint.setAttribute("id", "" + wpIdx++);
                xmlWaypoint.setAttribute("revision", "0");
                if (point.properties.name)
                {
                    xmlWaypoint.setAttribute("name", point.properties.name);
                }
                
                const xmlPosition = this._createXMLElement("position", xmlWaypoint);
                xmlPosition.setAttribute("lat", "" + point.geometry.coordinates[1]);
                xmlPosition.setAttribute("lon", "" + point.geometry.coordinates[0]);

                if (i > 0)
                {
                    const xmlLeg = this._createXMLElement("leg", xmlWaypoint);
                    xmlLeg.setAttribute("geometryType", route.route_geometry[i-1].properties.leg_type === "GreatCircle" ? "Orthodrome" : "Loxodrome");
                    
                    const prevPoint = <VoyageRoutePointFeature>route.route_geometry[i-2];
                    if (prevPoint.properties.speed_minimum)
                    {
                        xmlLeg.setAttribute("speedMin", ""+mpsToKnots(prevPoint.properties.speed_minimum))
                    }
                    if (prevPoint.properties.speed_maximum)
                    {
                        xmlLeg.setAttribute("speedMax", ""+mpsToKnots(prevPoint.properties.speed_maximum))
                    }
                }
            }
        }

        const xmlSer = new XMLSerializer();
        const result = '<?xml version="1.0" encoding="UTF-8"?>\n' + xmlSer.serializeToString(xmlDoc);

        return result;
    }

    private static _extractWP(wpEl: Element|null): { [index: string]: string } {
        const res: { [index: string]: any } = {};

        if (!wpEl) {
            return {};
        }

        for (let key of wpEl.getAttributeNames()) {
            res[key] = wpEl.getAttribute(key) || undefined;
        }

        // Leg
        const legEl = wpEl.querySelector('leg');
        if (legEl) {
            for (let key of legEl.getAttributeNames()) {
                res[`leg_${key}`] = legEl.getAttribute(key) || undefined;
            }
        }

        return res;
    }

    private static _getPosition(wpElement: Element): [number, number]
    {
        const posEl = wpElement.querySelector('position');
        
        if (!posEl)
        {
            throw new Error("Invalid RTZ format; position element is missing from waypoint");
        }

        const lat = posEl.getAttribute("lat");
        if (lat === null)
        {
            throw new Error("Invalid RTZ format; position element is missing lat attribute");
        }
        if (isNaN(Number(lat)))
        {
            throw new Error("Invalid RTZ format; lat attribute is not a number");
        }
        
        const lon = posEl.getAttribute("lon");
        if (lat === null)
        {
            throw new Error("Invalid RTZ format; position element is missing lon attribute");
        }
        if (isNaN(Number(lon)))
        {
            throw new Error("Invalid RTZ format; lon attribute is not a number");
        }
        
        return [Number(lon), Number(lat)];
    }

    private static _createXMLElement(name: string, parent: Element): Element
    {
        const xmlElement = document.createElementNS(parent.namespaceURI, name);
        parent.appendChild(xmlElement);
        return xmlElement;
    }
}